// Dot-path helpers + the AI → product field mapping used by the workbench.
import { SENSORY, CUP } from "../studioApi";

export const getPath = (o, p) => p.split(".").reduce((a, k) => (a == null ? undefined : a[k]), o);
export const setPath = (o, p, v) => {
  const keys = p.split(".");
  const out = { ...o };
  let cur = out;
  keys.forEach((k, i) => {
    if (i === keys.length - 1) cur[k] = v;
    else { cur[k] = { ...(cur[k] || {}) }; cur = cur[k]; }
  });
  return out;
};

// kind: "extracted" = read from the label (data); "observed" = visible appearance; "suggested" = AI draft wording (must be reviewed)
export const AI_MAP = [
  { from: "extracted.product_name", to: "name", label: "Product name", kind: "extracted" },
  { from: "extracted.manufacturer", to: "manufacturer", label: "Manufacturer", kind: "extracted" },
  { from: "extracted.origin", to: "origin", label: "Origin", kind: "extracted" },
  { from: "extracted.tea_type", to: "tea_type", label: "Tea type", kind: "extracted" },
  { from: "extracted.grade", to: "grade", label: "Grade", kind: "extracted" },
  { from: "extracted.invoice_no", to: "invoice_no", label: "Invoice no.", kind: "extracted" },
  { from: "extracted.lot_no", to: "lot_no", label: "Lot no.", kind: "extracted" },
  { from: "extracted.date", to: "date", label: "Date", kind: "extracted" },
  { from: "observations.dry_leaf", to: "sensory.dry_leaf.text", label: "Dry leaf (appearance)", kind: "observed" },
  { from: "observations.liquor", to: "sensory.liquor.text", label: "Liquor (appearance)", kind: "observed" },
  { from: "observations.wet_leaf", to: "sensory.wet_leaf.text", label: "Wet leaf (appearance)", kind: "observed" },
  { from: "suggestions.description", to: "description", label: "Short description", kind: "suggested" },
  { from: "suggestions.body", to: "body", label: "Body", kind: "suggested" },
  { from: "suggestions.strength", to: "strength", label: "Strength", kind: "suggested" },
  { from: "suggestions.briskness", to: "briskness", label: "Briskness", kind: "suggested" },
  { from: "suggestions.aroma", to: "aroma", label: "Aroma", kind: "suggested" },
  { from: "suggestions.taste_aroma", to: "sensory.taste_aroma.text", label: "Taste & aroma", kind: "suggested" },
  { from: "suggestions.colour", to: "cup.colour.text", label: "Cup colour", kind: "suggested" },
  { from: "suggestions.body", to: "cup.body.text", label: "Cup body", kind: "suggested" },
  { from: "suggestions.briskness", to: "cup.briskness.text", label: "Cup briskness", kind: "suggested" },
  { from: "suggestions.aroma", to: "cup.aroma.text", label: "Cup aroma", kind: "suggested" },
  { from: "suggestions.astringency", to: "cup.astringency.text", label: "Astringency", kind: "suggested" },
  { from: "suggestions.sweetness", to: "cup.sweetness.text", label: "Sweetness", kind: "suggested" },
  { from: "suggestions.mouthfeel", to: "cup.mouthfeel.text", label: "Mouthfeel", kind: "suggested" },
  { from: "suggestions.finish", to: "cup.finish.text", label: "Finish", kind: "suggested" },
  { from: "suggestions.milk_suitability_text", to: "milk_text", label: "Milk suitability note", kind: "suggested" },
  { from: "suggestions.buyer_fit", to: "buyer_fit", label: "Buyer fit", kind: "suggested" },
  { from: "suggestions.verdict", to: "verdict", label: "Taster’s verdict", kind: "suggested" },
];

const isEmpty = (v) => v == null || v === "" || (Array.isArray(v) && v.length === 0);
const same = (a, b) => JSON.stringify(a ?? "") === JSON.stringify(b ?? "");

// Proposals = every mapped AI value that differs from the current product value.
export function buildProposals(ai, product) {
  const out = [];
  for (const m of AI_MAP) {
    const v = getPath(ai, m.from);
    if (isEmpty(v)) continue;
    const cur = getPath(product, m.to);
    if (same(v, cur)) continue;
    out.push({ ...m, proposed: v, current: cur, currentEmpty: isEmpty(cur) });
  }
  return out;
}

// First run on a fresh product: fill only EMPTY fields, never overwrite anything typed by the user.
export function applyProposals(product, proposals, selectedPaths) {
  let p = product;
  const sources = { ...(product.field_sources || {}) };
  for (const pr of proposals) {
    if (!selectedPaths.has(pr.to)) continue;
    p = setPath(p, pr.to, pr.proposed);
    sources[pr.to] = pr.kind;
  }
  return { ...p, field_sources: sources };
}

export const SOURCE_META = {
  extracted: { label: "Read from label", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  observed: { label: "AI observed", cls: "bg-sky-100 text-sky-800 border-sky-300" },
  suggested: { label: "AI draft", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  scored: { label: "AI scored", cls: "bg-teal-100 text-teal-800 border-teal-300" },
  policy: { label: "Policy", cls: "bg-[#F1E5C9] text-[#153E32] border-[#AD8642]/50" },
  manual: { label: "Manual", cls: "bg-stone-100 text-stone-700 border-stone-300" },
};

// "Let AI score" response → proposals for the 3 result cards, 4 sensory and 8 cup cards (scores + captions).
export function buildScoreProposals(res, product) {
  const out = [];
  const push = (to, label, proposed, kind) => {
    if (isEmpty(proposed)) return;
    const cur = getPath(product, to);
    if (same(proposed, cur)) return;
    out.push({ to, label, kind, proposed, current: cur, currentEmpty: isEmpty(cur) });
  };
  const S = res.scores || {}, T = res.texts || {};
  if (res.anchor !== "manual") push("overall_rating", "Overall rating", S.overall, "policy");
  push("overall_text", "Overall rating caption", T.overall, "suggested");
  push("milk_score", "Milk suitability score", S.milk, "scored");
  push("milk_text", "Milk suitability note", T.milk, "suggested");
  push("value_score", "Commercial value score", S.value, "scored");
  push("value_text", "Commercial value note", T.value, "suggested");
  for (const [k, label] of SENSORY) { push(`sensory.${k}.score`, `${label} score`, S[`sensory.${k}`], "scored"); push(`sensory.${k}.text`, `${label} description`, T[`sensory.${k}`], "suggested"); }
  for (const [k, label] of CUP) { push(`cup.${k}.score`, `${label} score`, S[`cup.${k}`], "scored"); push(`cup.${k}.text`, `${label} description`, T[`cup.${k}`], "suggested"); }
  return out;
}

// Human-readable summary of the rules the engine applied (internal, shown in the review dialog).
export function describeScoreRules(res) {
  if (!res?.rules) return "";
  const r = res.rules;
  const head = res.anchor === "manual"
    ? `Anchored on your manual overall rating ${r.overall.min.toFixed(1)}.`
    : `Band ${res.band?.label || ""} → overall ${r.overall.min.toFixed(1)}–${r.overall.max.toFixed(1)}.`;
  const offs = Object.values(r.cards).map((c) => `${c.label} ${c.offset >= 0 ? "+" : "−"}${Math.abs(c.offset).toFixed(1)}`).join(" · ");
  return `${head} ${r.family === "CTC" ? "CTC" : r.family === "ORTHODOX" ? "Orthodox" : r.family === "GREEN" ? "Green" : "General"} profile — cards sit at overall + offset ± ${r.spread.toFixed(1)}: ${offs}.`;
}
